import React from "react";
import { useCotiPluginContext } from "@/context/PrivacyBridgeContext";
import { useAccount as useWagmiAccount, useSwitchChain } from "wagmi";
import NavbarConnectButton from "@/components/NavbarConnectButton";
import ThemeToggle from "@/components/ThemeToggle";
import ThemeLogo from "@/components/ThemeLogo";
import NetworkSelector, { DEFAULT_CHAINS } from "@/components/NetworkSelector";
import WalletMenu from "@/components/WalletMenu";

interface NavbarProps {
  showAdminBadge?: boolean;
  hideWalletButton?: boolean;
  /** Show NetworkSelector even when the connect button is hidden (e.g. welcome screen). */
  showNetworkSelector?: boolean;
  /** Hide the network control (e.g. when it is rendered above the wallet card instead). */
  hideNetworkSelector?: boolean;
  /** Welcome-screen chain pick — opens connect flow instead of switching an active wallet. */
  onSelectChain?: (chainHexId: string) => void;
  /** Override highlighted chain in NetworkSelector (e.g. pending welcome selection). */
  networkActiveChainId?: string;
  /** Override connect click (e.g. welcome screen connects on COTI by default). */
  onConnectClick?: () => void;
  /** Label for the welcome connect pill (default: Connect). */
  connectButtonLabel?: string;
  /** Welcome screen: show network pill as Connect instead of gradient button. */
  networkConnectMode?: boolean;
  /** Welcome screen: show NavbarConnectButton and ignore stale wagmi state after disconnect. */
  welcomeMode?: boolean;
}

const Navbar = ({
  showAdminBadge = false,
  hideWalletButton = false,
  showNetworkSelector = false,
  hideNetworkSelector = false,
  onSelectChain,
  networkActiveChainId,
  onConnectClick,
  connectButtonLabel = "Connect",
  welcomeMode = false,
  networkConnectMode = false,
}: NavbarProps) => {
  const {
    isConnected,
    walletAddress,
    chainId: pluginChainId,
    switchNetwork,
    handleDisconnect,
    lockPrivateBalances,
    isPrivateUnlocked,
  } = useCotiPluginContext();

  const { isConnected: wagmiConnected, chainId: wagmiChainId } = useWagmiAccount();
  const { switchChainAsync } = useSwitchChain();

  // lockPrivateBalances first: clears React session state AND the plugin's
  // validated-unlock Map. handleDisconnect alone skips that Map because it
  // sets wagmiSyncRef=false before the disconnect sync path can clear it.
  const handleForceDisconnect = async () => {
    try {
      console.log("[AES] Disconnect: clearing session key from memory", {
        wasPrivateUnlocked: Boolean(isPrivateUnlocked),
        walletAddress: walletAddress || null,
      });
      lockPrivateBalances?.();
      await handleDisconnect?.();
      console.log("[AES] Disconnect: lock + disconnect complete");
    } catch {
      // non-fatal
    }
  };

  // On the welcome screen, plugin isConnected is the source of truth — wagmi can
  // lag after disconnect and would otherwise keep controls in "connected" mode.
  const isWalletConnected = welcomeMode ? isConnected : (isConnected || wagmiConnected);
  const showWelcomeConnectPill = networkConnectMode && !welcomeMode && !isWalletConnected;
  const showNetworkSelectorInNavbar =
    !hideNetworkSelector &&
    !welcomeMode &&
    (showNetworkSelector || showWelcomeConnectPill || (!hideWalletButton && isWalletConnected));

  const handleSelectChain = (hexId: string) => {
    if (onSelectChain && !isWalletConnected) {
      onSelectChain(hexId);
      return;
    }

    const targetId = parseInt(hexId, 16);
    if (wagmiChainId !== targetId) {
      switchChainAsync({ chainId: targetId }).catch(() => {
        switchNetwork(hexId);
      });
    }
  };

  const activeChainHex = (
    networkActiveChainId ??
    (wagmiChainId != null
      ? `0x${wagmiChainId.toString(16)}`
      : pluginChainId
        ? (String(pluginChainId).startsWith("0x")
          ? String(pluginChainId)
          : `0x${Number(pluginChainId).toString(16)}`)
        : undefined)
  )?.toLowerCase();

  const walletMenuChainId = (() => {
    if (wagmiChainId != null) return wagmiChainId;
    if (pluginChainId == null || pluginChainId === "") return undefined;
    const raw = String(pluginChainId);
    const parsed = raw.startsWith("0x") || raw.startsWith("0X")
      ? parseInt(raw, 16)
      : Number(raw);
    return Number.isFinite(parsed) ? parsed : undefined;
  })();

  const welcomeConnectChains = showWelcomeConnectPill
    ? DEFAULT_CHAINS.filter((chain) => chain.id.toLowerCase() === activeChainHex)
    : undefined;
  // Never pass an empty list — NetworkSelector would crash without an active chain.
  const networkChains =
    welcomeConnectChains && welcomeConnectChains.length > 0
      ? welcomeConnectChains
      : undefined;

  return (
    <div className="relative z-50 w-full flex items-center justify-between p-4 md:p-6">
      {/* Left - Logo */}
      <div className="flex items-center gap-0.5">
        <ThemeLogo />
        {showAdminBadge && (
          <span className="bg-foreground/10 text-foreground text-xs font-medium px-2.5 py-1 rounded-full backdrop-blur-sm -ml-3">
            Admin
          </span>
        )}
      </div>

      {/* Right - Controls: theme | network | wallet */}
      <div className="flex items-center gap-2 md:gap-2.5">
        <ThemeToggle />

        {showNetworkSelectorInNavbar && (
          <NetworkSelector
            showLabel={false}
            menuAlign="right"
            activeChainId={activeChainHex}
            onSelectChain={handleSelectChain}
            connectMode={showWelcomeConnectPill}
            connectLabel={connectButtonLabel}
            onConnect={onConnectClick}
            chains={networkChains}
          />
        )}

        {isWalletConnected && walletAddress && !showWelcomeConnectPill && (
          <WalletMenu
            address={walletAddress}
            chainId={walletMenuChainId}
            onDisconnect={handleForceDisconnect}
            menuAlign="right"
          />
        )}

        {!hideWalletButton && !showWelcomeConnectPill && !isWalletConnected && (
          <NavbarConnectButton label={welcomeMode ? "Connect Wallet" : "COTI Wallet"} />
        )}
      </div>
    </div>
  );
};

export default Navbar;
